/**
 * Merge OneProfile skills (F2) into forecast rows — parity of `fmdashboard.controller.js`
 * `processProfileSkillsData` / the FetchManagerCalendar populate loop (~:344-362, :641-658):
 * profileCompleteness → Profile Completion %, skillCompleteness → Skill Assessment %, and the
 * latest `proficiencyAssignedDate` (ignoring the `0001` "never" sentinel) → Skills Last Updated.
 * Pure/side-effect-free so it is unit-tested and reused without the FXP shell.
 */
import { FMForecastRow } from '../FMForecastView/FMForecastView';
import { AssessedSkill, ResourceSkills } from '../../../contracts/FMOneProfileContracts';

/**
 * Latest `proficiencyAssignedDate` across assessed skills, ignoring the year-0001 sentinel and
 * unparseable dates (parity: the `skillDate.getFullYear() != 1` guard). Returns null when none.
 */
export function latestSkillDate(assessedSkills?: AssessedSkill[]): string | null {
  if (!Array.isArray(assessedSkills)) {
    return null;
  }
  let latest: string | null = null;
  for (const skill of assessedSkills) {
    if (!skill.proficiencyAssignedDate) {
      continue;
    }
    const d = new Date(skill.proficiencyAssignedDate);
    if (isNaN(d.getTime()) || d.getFullYear() === 1) {
      continue;
    }
    if (!latest || d > new Date(latest)) {
      latest = skill.proficiencyAssignedDate;
    }
  }
  return latest;
}

/**
 * Merge a `{ [alias]: ResourceSkills }` map into forecast rows (keyed case-insensitively by
 * `rowId`/alias). Rows with no matching skills are returned unchanged. Endorsement status is not
 * carried by the OpGraphQL skills query, so `endorsedStatus` is left as-is (→ NA/Not-Started logic).
 */
export function mergeSkillsIntoRows(
  rows: FMForecastRow[],
  skillsByAlias: Record<string, ResourceSkills | undefined>
): FMForecastRow[] {
  return rows.map((row) => {
    const skills = skillsByAlias[(row.rowId || '').toLowerCase()];
    if (!skills) {
      return row;
    }
    return {
      ...row,
      profileCompletionPercentage: skills.profileCompleteness,
      skillAssessmentPercentage: skills.skillCompleteness,
      lastSkillsUpdated: latestSkillDate(skills.assessedSkills),
    };
  });
}
